import React, { ReactNode } from 'react';

import { margin, useClassnames } from '@lumapps/classnames';
import { InlineDialog, InlineDialogProps } from '@lumapps/lumx-dialogs/components/InlineDialog';
import {
    ChipProps,
    DropdownProps,
    Button,
    ButtonProps,
    Emphasis,
    Tooltip,
    Placement,
    FlexBox,
    Theme,
    TooltipProps,
} from '@lumapps/lumx/react';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { FILTERS_MODE } from '../../constants';
import { useFilterLabel } from '../../hooks/useFilterLabel';
import { FilterTrigger } from '../FilterTrigger';

import './index.scss';

export interface FiltersDropdownProps {
    /** callback to be executed once the clear button on the main CTA is clicked */
    onClearClick: () => void;
    /** callback to be executed once the dropdown is closed */
    onClose: () => void;
    /** callback to be executed once the filter button is clicked */
    onFilter: () => void;
    /** callback to be executed once the clear button is clicked. */
    onClearAll?: () => void;
    /** callback executed once the filters are displayed */
    onOpen?: () => void;
    /** override the label displayed for the chip that will open the dropdown  */
    label?: string;
    /** title of the dropdown, it default to GLOBAL.FILTERS */
    title?: string;
    /** whether the dropdown is open or not */
    isOpen?: boolean;
    /** whether the dropdown is disabled or not */
    isDisabled?: boolean;
    /** list of selected filters */
    selectedFilter?: string[];
    /** props to be passed down to the dropdown trigger */
    triggerProps?: Partial<ChipProps>;
    /** props to be passed down to the dropdown */
    dropdownProps?: Partial<DropdownProps>;
    /** props to be passed down to the filter button */
    filterProps?: Partial<ButtonProps>;
    /** type of mode currently applied */
    mode?: FILTERS_MODE;
    /** additional props for the tooltip */
    tooltipProps?: Partial<TooltipProps>;
    /** additional props for the inline dialog */
    inlineDialogProps?: Partial<InlineDialogProps>;
    /** children to display */
    children?: ReactNode;
    /** theme */
    theme?: Theme;
}

const CLASSNAME = 'filters-dropdown';
const FORM_ID = `${CLASSNAME}-form`;
/**
 * Component that displays a list of filters inside a dropdown and manages the list of selected ones.
 * This component is highly customisable, so use only if you need to do some custom specific customisations.
 * If your filters are pretty straightforward, please use `Filters`.
 *
 * @family Filters
 * @param FiltersDropdownProps
 * @returns FiltersDropdown
 */
export const FiltersDropdown: React.FC<FiltersDropdownProps> = ({
    title,
    label,
    isOpen = false,
    onFilter,
    selectedFilter = [],
    children,
    onClose,
    triggerProps,
    dropdownProps,
    inlineDialogProps,
    filterProps,
    isDisabled = false,
    tooltipProps,
    onClearAll,
    onClearClick,
    onOpen,
    mode,
    theme = Theme.light,
}) => {
    const { element } = useClassnames(CLASSNAME);
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(isOpen);
    const { translateKey } = useTranslate();
    const ref = React.useRef<HTMLAnchorElement>(null);

    const { filtersLabel } = useFilterLabel({
        label,
        selectedFilter,
    });

    React.useEffect(() => {
        setIsDropdownOpen(isOpen);
    }, [isOpen]);

    const onFilterClick = (event: any) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        onFilter();
        setIsDropdownOpen(false);
    };

    return (
        <Tooltip label={filtersLabel} placement={Placement.TOP} {...tooltipProps}>
            <FilterTrigger
                setIsFilterDropdownOpen={(open, event) => {
                    if (open && onOpen) {
                        onOpen();
                    } else if (!open) {
                        onClose();
                    }

                    if (event.key === 'Enter') {
                        /**
                         * for some reason I cannot explain, if this set timeout is not added,
                         * the dropdown won't open if you are just using your keyboard.
                         */
                        setTimeout(() => setIsDropdownOpen(open));
                    } else {
                        setIsDropdownOpen(open);
                    }
                }}
                isDisabled={isDisabled}
                isFilterDropdownOpen={isDropdownOpen}
                label={label}
                selectedFilter={selectedFilter}
                triggerProps={triggerProps}
                anchorRef={ref}
                theme={theme}
            />

            <InlineDialog
                setIsOpen={setIsDropdownOpen}
                isOpen={isDropdownOpen}
                className={CLASSNAME}
                anchorRef={ref}
                onClose={() => {
                    onClose();
                    setIsDropdownOpen(false);
                }}
                title={title || translateKey(GLOBAL.FILTERS)}
                popoverProps={dropdownProps}
                wrapperProps={{
                    as: 'form',
                    className: element('filters'),
                    id: FORM_ID,
                    onSubmit: onFilterClick,
                    // Disable form auto validation and manage it ourselves
                    noValidate: true,
                }}
                footerProps={{
                    className: element('footer', { 'with-clear': Boolean(onClearAll) }),
                    before:
                        onClearAll || onClearClick ? (
                            <Button
                                emphasis={Emphasis.low}
                                onClick={() => {
                                    setIsDropdownOpen(false);

                                    (onClearAll || onClearClick)();
                                }}
                                className={margin('right')}
                                isDisabled={selectedFilter.length === 0}
                            >
                                {mode === FILTERS_MODE.GROUPED
                                    ? translateKey(GLOBAL.CLEAR_ALL)
                                    : translateKey(GLOBAL.CLEAR)}
                            </Button>
                        ) : null,
                    after: (
                        <Button
                            form={FORM_ID}
                            type="submit"
                            /* eslint-disable-next-line react/no-children-prop */
                            children={translateKey(GLOBAL.APPLY)}
                            {...filterProps}
                            onClick={onFilterClick}
                        />
                    ),
                }}
                {...inlineDialogProps}
            >
                <div className={element('inner')}>
                    <FlexBox orientation="vertical" gap="huge">
                        {children}
                    </FlexBox>
                </div>
            </InlineDialog>
        </Tooltip>
    );
};
